export type QpCodec<T> = {
  parse: (raw: string | null) => T;
  serialize: (value: T) => string | null;
};

const string = (fallback = ''): QpCodec<string> => ({
  parse: (raw) => raw ?? fallback,
  serialize: (value) => (value === fallback ? null : value),
});

const id = (): QpCodec<string | null> => ({
  parse: (raw) => raw || null,
  serialize: (value) => value || null,
});

const enumValue = <T extends string>(allowed: readonly T[], fallback: T): QpCodec<T> => {
  const allowedValues = new Set<string>(allowed);

  return {
    parse: (raw) => (raw !== null && allowedValues.has(raw) ? (raw as T) : fallback),
    serialize: (value) => (value === fallback ? null : value),
  };
};

const csvEnum = <T extends string>(allowed: readonly T[]): QpCodec<T[]> => {
  const allowedValues = new Set<string>(allowed);

  return {
    parse: (raw) => {
      if (!raw) return [];

      return [...new Set(raw.split(',').filter((value): value is T => allowedValues.has(value)))];
    },
    serialize: (value) => {
      const normalized = [...new Set(value.filter((item) => allowedValues.has(item)))];
      return normalized.length > 0 ? normalized.join(',') : null;
    },
  };
};

const bool = (fallback = false): QpCodec<boolean> => ({
  parse: (raw) => (raw === null ? fallback : raw === 'true'),
  serialize: (value) => (value === fallback ? null : String(value)),
});

export const qp = {
  string,
  id,
  enum: enumValue,
  csvEnum,
  bool,
};
